import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import { PdfjsWorkerBootstrapService } from 'src/app/services/pdfjs-worker-bootstrap.service';

interface PageSize {
  width: number;
  height: number;
}

@Component({
  selector: 'app-pdf-virtual-viewer',
  templateUrl: './pdf-virtual-viewer.component.html',
  styleUrls: ['./pdf-virtual-viewer.component.scss'],
  standalone: false,
})
export class PdfVirtualViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  // Accepts either the raw decoded bytes (preferred — avoids holding a
  @Input() pdfData: Uint8Array | string | null = null; 
 
  @Input() scrollRoot?: HTMLElement | string | null;

  
  @Input() bufferPx = 1600;
  
  @Input() overlayPages?: Set<number> | null;

  @Output() docLoaded = new EventEmitter<{ numPages: number }>();
  @Output() pageRendered = new EventEmitter<{
    pageNum: number;
    widthPx: number;
    heightPx: number;
    widthPt: number;
    heightPt: number;
  }>();

  @ContentChild(TemplateRef) overlayTemplate?: TemplateRef<any>;
  @ViewChild('pagesContainer') pagesContainerRef?: ElementRef<HTMLDivElement>;
  @ViewChildren('pageWrapperEl') private wrapperRefs!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('canvasEl') private canvasRefs!: QueryList<ElementRef<HTMLCanvasElement>>;

  pageNumbers: number[] = [];
  readonly renderedPages = new Set<number>();
  private pageSizes: (PageSize | undefined)[] = [];
  private readonly DEFAULT_WIDTH_PT = 595;
  private readonly DEFAULT_HEIGHT_PT = 842;

  private pdfDoc: any = null;
  private readonly renderTasks = new Map<number, any>();
  private observer?: IntersectionObserver;
  private wrapperSub?: { unsubscribe(): void };

  constructor(
    private readonly pdfjsWorkerBootstrap: PdfjsWorkerBootstrapService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdfData'] && this.pdfData) {
      this.loadDocument();
    }
  }

  ngAfterViewInit(): void {
    this.wrapperSub = this.wrapperRefs.changes.subscribe(() => this.attachObserver());
    this.attachObserver();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.wrapperSub?.unsubscribe();
    this.renderTasks.forEach((task) => task.cancel());
    this.renderTasks.clear();
    this.pdfDoc?.destroy();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.attachObserver();
  }

  trackByPage(_index: number, pageNum: number): number {
    return pageNum;
  }

  getPageWidthPx(pageNum: number): number {
    const size = this.pageSizes[pageNum - 1];
    return this.convertPtToPx(size ? size.width : this.DEFAULT_WIDTH_PT);
  }

  getPageHeightPx(pageNum: number): number {
    const size = this.pageSizes[pageNum - 1];
    return this.convertPtToPx(size ? size.height : this.DEFAULT_HEIGHT_PT);
  }

  // Gated the same way canvases are (only pages currently within the
  // visible+buffer window) — without this, a document-wide overlay (no
  // overlayPages restriction, e.g. drag-drop's click-to-place layer) would
  // instantiate one embedded view per page up front, reintroducing the same
  // freeze this component exists to avoid.
  showOverlayFor(pageNum: number): boolean {
    if (!this.overlayTemplate) return false;
    if (this.overlayPages && !this.overlayPages.has(pageNum)) return false;
    return this.renderedPages.has(pageNum);
  }

  // Preserves the existing scrollIntoView/data-page-number contract so
  // consumers' "jump to signature page" logic keeps working unmodified.
  scrollToPage(pageNum: number): void {
    setTimeout(() => {
      this.pagesContainerRef?.nativeElement
        .querySelector(`[data-page-number="${pageNum}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private convertPtToPx(pt: number): number {
    return (4 / 3) * pt;
  }

  private async loadDocument(): Promise<void> {
    await this.pdfjsWorkerBootstrap.ready;

    this.observer?.disconnect();
    this.renderTasks.forEach((task) => task.cancel());
    this.renderTasks.clear();
    this.renderedPages.clear();
    this.pdfDoc?.destroy();

    const data = this.normalizeInput(this.pdfData);
    if (!data) return;

    this.pdfDoc = await pdfjsLib.getDocument({ data }).promise;
    const numPages: number = this.pdfDoc.numPages;
    this.pageSizes = new Array(numPages);
    this.pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);
    this.docLoaded.emit({ numPages });
    this.cdr.detectChanges();
    this.attachObserver();
  }

  private normalizeInput(data: Uint8Array | string | null): Uint8Array | null {
    if (!data) return null;
    if (data instanceof Uint8Array) return data;

    const base64 = data.includes(',') ? data.split(',')[1] : data;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private resolveScrollRoot(): Element | null {
    let el: HTMLElement | null = null;
    if (typeof this.scrollRoot === 'string') {
      el = document.querySelector(this.scrollRoot);
    } else if (this.scrollRoot instanceof HTMLElement) {
      el = this.scrollRoot;
    }
    if (!el) return null;

    // Some scroll panes fall back to viewport-level scrolling below a
    // breakpoint (e.g. mobile) — don't hand IntersectionObserver a "root"
    // that isn't actually the scrolling ancestor, it would never intersect.
    const style = window.getComputedStyle(el);
    const scrolls = style.overflowY === 'auto' || style.overflowY === 'scroll';
    return scrolls ? el : null;
  }

  private attachObserver(): void {
    if (!this.wrapperRefs || this.wrapperRefs.length === 0) return;

    this.observer?.disconnect();
    this.observer = new IntersectionObserver(this.onIntersect, {
      root: this.resolveScrollRoot(),
      rootMargin: `${this.bufferPx}px 0px`,
      threshold: 0,
    });
    this.wrapperRefs.forEach((ref) => this.observer!.observe(ref.nativeElement));
  }

  private onIntersect = (entries: IntersectionObserverEntry[]): void => {
    const toRender: number[] = [];
    const toCleanup: number[] = [];

    for (const entry of entries) {
      const pageNum = Number((entry.target as HTMLElement).dataset['pageNumber']);
      if (!pageNum) continue;
      if (entry.isIntersecting) {
        if (!this.renderedPages.has(pageNum)) toRender.push(pageNum);
      } else if (this.renderedPages.has(pageNum)) {
        toCleanup.push(pageNum);
      }
    }

    for (const pageNum of toCleanup) {
      this.renderTasks.get(pageNum)?.cancel();
      this.renderTasks.delete(pageNum);
      this.renderedPages.delete(pageNum);
    }
    if (toCleanup.length) this.cdr.detectChanges();

    if (toRender.length) {
      toRender.forEach((pageNum) => this.renderedPages.add(pageNum));
      this.cdr.detectChanges();
      toRender.forEach((pageNum) => this.renderPageContent(pageNum));
    }
  };

  private async renderPageContent(pageNum: number): Promise<void> {
    if (!this.pdfDoc || !this.renderedPages.has(pageNum)) return;

    let page: any;
    try {
      page = await this.pdfDoc.getPage(pageNum);
    } catch {
      return;
    }
    if (!this.renderedPages.has(pageNum)) {
      page.cleanup?.();
      return;
    }

    const baseViewport = page.getViewport({ scale: 1 });
    const measured: PageSize = { width: baseViewport.width, height: baseViewport.height };
    const prev = this.pageSizes[pageNum - 1];
    const sizeChanged = !prev || prev.width !== measured.width || prev.height !== measured.height;
    this.pageSizes[pageNum - 1] = measured;
    if (sizeChanged) this.cdr.detectChanges();

    const canvasRef = this.canvasRefs?.find(
      (c) => Number(c.nativeElement.dataset['pageNumber']) === pageNum,
    );
    if (!canvasRef || !this.renderedPages.has(pageNum)) return;

    const cssWidth = this.getPageWidthPx(pageNum);
    const cssHeight = this.getPageHeightPx(pageNum);
    const dpr = window.devicePixelRatio || 1;
    const scale = (cssWidth * dpr) / measured.width;
    const viewport = page.getViewport({ scale });

    const canvas = canvasRef.nativeElement;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderTask = page.render({ canvasContext: ctx, viewport });
    this.renderTasks.set(pageNum, renderTask);
    try {
      await renderTask.promise;
      this.pageRendered.emit({
        pageNum,
        widthPx: cssWidth,
        heightPx: cssHeight,
        widthPt: measured.width,
        heightPt: measured.height,
      });
    } catch {
      // Expected when the page scrolled out of view mid-render and the
      // task was cancelled in onIntersect/ngOnDestroy.
    } finally {
      this.renderTasks.delete(pageNum);
    }
  }
}
